import React, { useEffect, useState } from 'react'
import CustomPagination from '../../../../components/Pagination/CustomPagination';
import FilterModal from '../../../Reports/FilterModal/FilterModal';
import { isAuthorised } from '../../../../utils/common';
import FunnelIconSvg from '../../../../components/SVG/FunnelIconSvg';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAllApplicationsForOfficer } from '../../../../redux/BranchReducer/action';
import { getApplicationStatusList } from '../../../../redux/ApplicationReducer/action';
import { getOfficers } from '../../../../redux/UserReducer/action';
import { resetFilters } from '../../../../utils/reportUtils';
import { TEAM_OVERVIEW_COMPONENTS } from '../../../../config/team_overview';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { handleCopyToClipboard } from '../../../../utils/common';
import './ApplicationView.css';

const ApplicationView = ({ branchOptions, bwApplicationListFilters, setBwApplicationListFilters, branchApplicationFilters, setBranchApplicationFilters, handlePageChange, currentPage, limit, totalPages }) => {
    const { applications, pagination, officers, applicationsSummary, applicationStatusList, officerProfile } = useSelector((store) => {
        return {
            applications: store.BranchReducer.branchApplications.data,
            pagination: store.BranchReducer.branchApplications.pagination,
            officers: store.UserReducer.officers.officers,
            applicationsSummary: store.BranchReducer.applicationsSummary,
            applicationStatusList: store.ApplicationReducer.applicationStatusList,
            officerProfile: store.UserReducer.officerProfile,
        }
    }, []);

    // const [currentPage, setCurrentPage] = useState(parseInt(sessionStorage.getItem('currentPage')) || 1);
    // const [limit, setLimit] = useState(parseInt(sessionStorage.getItem('limit')) || 10);
    // const totalCount = parseInt(sessionStorage.getItem('totalCount')) || 0;
    // const totalPages = Math.ceil(totalCount / limit);

    const [showFilterModal, setShowFilterModal] = useState(false);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();

    useEffect(() => {
        let cleanFilters = { ...branchApplicationFilters };
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (uuidRegex.test(cleanFilters.seeds_id?.trim() || "") || cleanFilters.seeds_id?.includes("...")) {
            cleanFilters.seeds_id = "";
        }
        dispatch(getAllApplicationsForOfficer(currentPage, limit, cleanFilters));

        sessionStorage.setItem('currentPage', currentPage);
        sessionStorage.setItem('limit', limit);
        sessionStorage.setItem('branchApplicationFilters', JSON.stringify(branchApplicationFilters));
    }, [dispatch, limit, currentPage, branchApplicationFilters]);

    useEffect(() => {
        // Fetched without a branch_name filter, same as Rejected Applications
        // and Branch Dashboard - that value has to exactly match the "o.branch"
        // string column on the backend with no guarantee it would, which is why
        // the RO/BM/BCM Employee ID filter options were coming back empty.
        dispatch(getOfficers(1, 1000, { is_active: true }));
    }, [dispatch]);

    useEffect(() => {
        dispatch(getApplicationStatusList({
            is_active: true
        }));
    }, [dispatch]);

    useEffect(() => {
        if (location.state?.filters) {
            setBranchApplicationFilters(location.state.filters);
        }
    }, []);

    const handleCopyApplicationNumber = async (e, seedsId) => {
        e.preventDefault();

        if (!seedsId) return;

        const { success } = await handleCopyToClipboard(seedsId);

        if (success) {
            toast.success("Application number copied to clipboard!");
        }
    };

    const handleFilterChange = (e) => {
        let { name, value } = e.target;

        if (typeof value === 'string') {
            value = value.replace(/[\t\n\r]+/g, '').replace(/^\s+/, '');
        }

        let updatedFilters = {
            ...branchApplicationFilters,
            [name]: value
        };

        // If parent stage changes → reset application stage
        if (name === "current_application_bucket") {
            updatedFilters.application_stage_name = "";
        }

        setBranchApplicationFilters(updatedFilters);
        handlePageChange(1);
        sessionStorage.setItem("branchApplicationFilters", JSON.stringify(updatedFilters));
    }

    // const handlePageChange = (newPage) => {
    //     // Ensure we're working with 1-based pagination
    //     const pageToSet = Math.max(1, Math.min(newPage, totalPages));
    //     setCurrentPage(pageToSet);
    //     sessionStorage.setItem('currentPage', pageToSet.toString());
    // };

    // RO/BM/BCM Employee IDs options for the All Filters panel, grouped from
    // the officer list by role - same construction as Rejected Applications
    // and Branch Dashboard. `officers` is an array of branch-groups (one per
    // branch the unscoped fetch above returned), each with its own
    // officer_details - iterate every group instead of assuming a single
    // element at officers[0].
    const officerOptions = React.useMemo(() => {
        const roOptions = [];
        const bmOptions = [];
        const bcmOptions = [];

        officers?.forEach(branch => {
            branch?.officer_details?.forEach(officer => {
                const option = {
                    label: `${officer.emp_id} - ${officer.first_name} ${officer.last_name || ""}`.trim(),
                    value: officer.emp_id
                };

                if (officer.role_code === "RO") {
                    roOptions.push(option);
                }

                if (officer.role_code === "BM") {
                    bmOptions.push(option);
                }

                if (officer.role_code === "BCM") {
                    bcmOptions.push(option);
                }
            });
        });

        // remove duplicate emp_ids
        const uniqueByValue = (arr) => {
            return arr.filter(
                (item, index, self) =>
                    index === self.findIndex(obj => obj.value === item.value)
            );
        };

        return {
            roOptions: uniqueByValue(roOptions),
            bmOptions: uniqueByValue(bmOptions),
            bcmOptions: uniqueByValue(bcmOptions),
        };
    }, [officers]);

    const masterStatusList = applicationStatusList || [];

    // Distinct Parent Stages
    const parentStageOptions = React.useMemo(() => {
        return [
            ...new Set(
                masterStatusList?.map(item => item.parent_stage_name)
            )
        ]
            .map(stage => ({
                label: stage,
                value: stage
            }));
    }, [masterStatusList]);

    // Application Stages based on selected parent
    const applicationStageOptions = React.useMemo(() => {
        // If no parent selected → show all application stages
        if (!branchApplicationFilters.current_application_bucket) {
            return [
                ...new Set(
                    masterStatusList?.map(item => item.application_stage_name)
                )
            ]
                .map(stage => ({
                    label: stage,
                    value: stage
                }));
        }

        // If parent selected → show only related application stages
        return [
            ...new Set(
                masterStatusList
                    ?.filter(
                        item =>
                            item.parent_stage_name ===
                            branchApplicationFilters.current_application_bucket
                    )
                    ?.map(item => item.application_stage_name)
            )
        ]
            .map(stage => ({
                label: stage,
                value: stage
            }));
    }, [masterStatusList, branchApplicationFilters.current_application_bucket]);

    return (
        <div>
            <div className="teamAppPageHeaderSection">
                <div className="searchControlSection">
                    <div className="tableSearchBar">
                        <input
                            type="search"
                            placeholder="Search by customer, application id"
                            name="global_search"
                            value={branchApplicationFilters?.global_search}
                            onChange={handleFilterChange}
                            onInput={(e) => {
                                if (e.target.value === "") {
                                    let updatedFilters = {
                                        ...branchApplicationFilters,
                                        global_search: e.target.value
                                    }

                                    setBranchApplicationFilters(updatedFilters);
                                    handlePageChange(1);

                                    dispatch(getAllApplicationsForOfficer(1, limit, updatedFilters));
                                }
                            }}
                        />
                    </div>
                    <div className="searchActionButtons">
                        <button
                            className="teamSearchBtn"
                            onClick={() => {
                                dispatch(getAllApplicationsForOfficer(1, limit, branchApplicationFilters, null));
                            }}
                        >
                            <FontAwesomeIcon icon={faSearch} />
                            <span>Search</span>
                        </button>
                        {
                            isAuthorised(officerProfile, TEAM_OVERVIEW_COMPONENTS?.view_applications_filter) && (
                                <button
                                    className="filterButton"
                                    onClick={() => setShowFilterModal(true)}
                                >
                                    <FunnelIconSvg fillColor={"black"} />
                                </button>
                            )
                        }
                    </div>
                </div>
            </div>

            {
                showFilterModal && (
                    <FilterModal
                        reportTitle={"branch-applications"}
                        filters={branchApplicationFilters}
                        setFilters={setBranchApplicationFilters}
                        resetFilters={() => {
                            let resettedFilters = resetFilters(branchApplicationFilters);

                            setBranchApplicationFilters(resettedFilters);
                            dispatch(getAllApplicationsForOfficer(1, limit, resettedFilters, null));
                            setShowFilterModal(false);
                            handlePageChange(1);
                            sessionStorage.setItem("branchApplicationFilters", JSON.stringify(resettedFilters));
                            return resettedFilters;
                        }}
                        handleApplyFilters={(finalFilters) => {
                            handlePageChange(1);
                            if (finalFilters?.branch_code) {
                                setBwApplicationListFilters(prev => ({
                                    ...prev,
                                    branch_code: finalFilters?.branch_code
                                }))
                            }
                            // The data fetch itself is handled by the effect above,
                            // which is keyed on branchApplicationFilters - setFilters
                            // (called by FilterModal just before this) triggers it.
                            setShowFilterModal(false);
                        }}
                        onClose={() => {
                            setShowFilterModal(false)
                        }}
                        moduleName={"Officer Applications"}
                        dynamicOptions={{
                            ro_employee_ids: officerOptions.roOptions,
                            bm_employee_ids: officerOptions.bmOptions,
                            bcm_employee_ids: officerOptions.bcmOptions,
                            current_application_bucket: parentStageOptions,
                            application_stage_name: applicationStageOptions,
                            branch_code: branchOptions
                        }}
                    />
                )
            }

            <div className="applicationsTableContainer">
                <table className="applications-table">
                    <thead>
                        <tr>
                            <th>Application Number</th>
                            <th>Application ID</th>
                            <th>RO Name</th>
                            <th>RO ID</th>
                            <th>BM Name</th>
                            <th>Customer Name</th>
                            <th>Customer ID</th>
                            <th>Branch ID</th>
                            <th>Branch</th>
                            <th>Parent Stage</th>
                            <th>Application Stage</th>
                            <th>Application Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            applications?.length === 0 ?
                                <tr>
                                    <td colSpan={12} className="no-data-cell">
                                        No records found
                                    </td>
                                </tr> :
                                applications?.map((application, index) => {
                                    const ROName = `${application?.officers?.ro?.first_name}${application?.officers?.ro?.last_name ? ` ${application?.officers?.ro?.last_name}` : ''}` || "";
                                    const BMName = `${application?.officers?.bm?.first_name}${application?.officers?.bm?.last_name ? ` ${application?.officers?.bm?.last_name}` : ''}` || "";

                                    return (
                                        <tr key={application?.application_id}>
                                            <td
                                                className="redirect-link"
                                                onClick={() => {
                                                    // A drag-to-select still fires a click on mouseup — don't navigate
                                                    // away in the middle of the user copying the text.
                                                    if (window.getSelection()?.toString()) return;

                                                    // navigate(`/applications/${application.application_id}`, { state: { from: location.pathname }})

                                                    navigate(`/applications/${application.application_id}?from=${encodeURIComponent(location.pathname + location.search)}`);
                                                }}
                                                onContextMenu={(e) => handleCopyApplicationNumber(e, application?.seeds_id)}
                                                title="Right-click to copy"
                                            >
                                                {application?.seeds_id}
                                            </td>
                                            <td>{application?.application_id}</td>
                                            <td>{ROName}</td>
                                            <td>{application?.ro_id}</td>
                                            <td>{BMName}</td>
                                            <td>{`${application?.first_name || ""}${application?.middle_name ? ` ${application?.middle_name}` : ""}${application?.last_name ? ` ${application?.last_name}` : ""}`}</td>
                                            <td>{application?.external_customer_id}</td>
                                            <td>{application?.branch_code}</td>
                                            <td>{application?.branch_name}</td>
                                            <td>{application?.parent_stage_name}</td>
                                            <td>{application?.application_stage_name}</td>
                                            <td>{application?.createdAt?.split('T')?.[0]}</td>
                                        </tr>
                                    )
                                })
                        }
                    </tbody>
                </table>
            </div>

            {applications?.length > 0 && <div className="paginationControls">
                <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                />
            </div>}
        </div>
    );
}

export default ApplicationView